import { useDebouncedCallback } from '@/hooks';
import { PalmettoIcon, PalmettoTag } from '@palmetto/dsm-components';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { RichTextNode } from 'types';
import {
  ChecklistItemStatus,
  type ChecklistItemInputDefinitionView,
  type ChecklistPendingFlagDecision,
  ChecklistItemInputType,
  FlagPurposeType,
  getEvaluationCore,
} from 'types';
import type { EvaluationState } from 'types';

import { ChecklistItemInputs } from './ChecklistItemInputs';
import { useChecklistTooltip } from './ChecklistTooltipDock';

const EMPTY_ITEM_DATA = {};

// Safety-net delay for persisting a pending draft — NOT the primary persistence trigger.
// The primary triggers are explicit: a text input's blur, a RequirementSelect's close, or an
// authoritative action's own cancel()+save. This delay only exists so a draft still reaches
// the server if the item card unmounts before either fires; it's intentionally much longer
// than a normal typing pause so it never fires mid-edit and reintroduces the periodic-PATCH
// traffic this mechanism is meant to avoid.
const DRAFT_SAFETY_NET_DEBOUNCE_MS = 3000;

export interface FlagDecision {
  purposeType: FlagPurposeType;
  label: string;
  requirementFlagSettingId?: string;
  checklistInputKey?: string;
  note?: string;
  // Set on decisions produced by a locally-toggled requirement option (FIN-4866 multi-flag
  // support) — identifies which input/option this decision came from so ChecklistPanel can
  // tag the resulting Flag's `checklistInputKey`/`checklistOptionValue`. Evaluation-produced
  // decisions above use `checklistInputKey`/`note` instead and never set these.
  inputKey?: string;
  optionValue?: string;
}

/** A persisted, currently-open flag backing one selected option on a requirement input. */
export interface ActiveItemFlag {
  flagId: string;
  value: string;
}

// Shared action-button styling — keeps the pill buttons DRY and token-based.
const ACTION_BUTTON_BASE =
  'flex items-center justify-center gap-[8px] rounded-[16px] px-[20px] py-[14px] font-bold disabled:opacity-50 cursor-pointer hover:opacity-80 ';
const PRIMARY_ACTION_BUTTON = `${ACTION_BUTTON_BASE} bg-[var(--btn-bg-primary-idle)]`;
const SUCCESS_ACTION_BUTTON = `${ACTION_BUTTON_BASE} bg-[var(--canvas-success)]`;
const COMPLETE_LABEL_CLASS = 'text-[var(--text-on-inverted-primary)]';

export interface ChecklistItemCardProps {
  itemKey: string;
  name: string;
  description: string;
  tooltipText?: RichTextNode[];
  status: ChecklistItemStatus;
  aiCompleted?: boolean;
  aiSummary?: string;
  inputs?: ChecklistItemInputDefinitionView[];
  savedData?: Record<string, string | boolean | ChecklistPendingFlagDecision[]>;
  isExpanded: boolean;
  flagCount?: number;
  onToggleExpand: (itemKey: string) => void;
  onApprove: (
    itemKey: string,
    data: Record<string, string | boolean>,
    evaluations: Record<string, EvaluationState>,
  ) => void;
  onStartFlagFlow: (
    itemKey: string,
    data: Record<string, string | boolean>,
    flagDecisions: FlagDecision[],
    evaluations: Record<string, EvaluationState>,
  ) => void;
  onUnreview: (itemKey: string) => void;
  isUpdating: boolean;
  isOptimisticInProgress?: boolean;
  // This item's currently-open flags, grouped by `checklistInputKey`. Threaded from
  // ChecklistPanel, which reads them back from queue.flags.
  activeFlagsByInputKey?: Record<string, ActiveItemFlag[]>;
  onRemoveFlag?: (flagId: string) => void;
  savedEvaluations?: Record<string, EvaluationState>;
  // Persists a pending draft to the checklist item's `data` bag — the same field/PATCH the
  // approved path already uses — as edits happen: a terminal decision, a text input blur, a
  // flag-producing selection, or an evaluation check. Fires well before "Complete"/"Save
  // Flag(s)" is clicked, so nothing requires that click to survive navigation. `evaluations`,
  // when present, carries any pending evaluation-state changes through the same channel
  // `onUpdateItem` already accepts as its 4th parameter. The item's status is never changed
  // by this call.
  onPersistDraft?: (
    itemKey: string,
    data: Record<string, string | boolean | ChecklistPendingFlagDecision[]>,
    evaluations?: Record<string, EvaluationState>,
  ) => void;
}

export const ChecklistItemCard: React.FC<ChecklistItemCardProps> = ({
  itemKey,
  name,
  description,
  tooltipText,
  status,
  aiCompleted = false,
  aiSummary,
  inputs,
  savedData,
  isExpanded,
  flagCount,
  onToggleExpand,
  onApprove,
  onStartFlagFlow,
  onUnreview,
  isUpdating,
  isOptimisticInProgress = false,
  activeFlagsByInputKey,
  onRemoveFlag,
  savedEvaluations,
  onPersistDraft,
}) => {
  // `inputData` holds only terminal (string|boolean) decisions — a ChecklistPendingFlagDecision[]
  // draft in `savedData` is not a terminal value, so it's excluded here and seeded into
  // `pendingFlagSelections` below instead.
  const [inputData, setInputData] = useState<Record<string, string | boolean>>(() => {
    const seeded: Record<string, string | boolean> = {};
    for (const [key, value] of Object.entries(savedData ?? {})) {
      if (!Array.isArray(value)) seeded[key] = value;
    }
    return seeded;
  });
  // Locally-toggled (unsaved) flag-producing option selections, keyed by input key. Mirrors
  // ChecklistItemInputs' own internal state via `onFlagSelectionsChange` — never mixed into
  // `inputData`/`data` (flag selections never enter the terminal-decision bag). Seeded from
  // any persisted ChecklistPendingFlagDecision[] draft in `savedData` so a rejection/
  // stipulation selection survives an unmount/remount (e.g. navigating away and back).
  const [pendingFlagSelections, setPendingFlagSelections] = useState<Record<string, string[]>>(() => {
    const seeded: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(savedData ?? {})) {
      if (Array.isArray(value)) seeded[key] = value.map((decision) => decision.optionValue);
    }
    return seeded;
  });
  // Evaluation inputs report structured state rather than the scalar `data` bag, so seed
  // each from its response view. The GET/hydrate path already recomputed `verdict`/
  // `rows[].verdict` server-side against live `derived` before redacting any fields for the
  // wire (e.g. serialValidation's Annex II reference) — trust it as-is rather than
  // recomputing locally, which would silently downgrade an already-correct verdict whenever
  // a field it depends on isn't present on the client.
  // Prefer savedEvaluations (captured when Create flag was clicked) over inputs, since the
  // RTK refetch may not have completed yet when this component remounts after the flag flow.
  const [evaluations, setEvaluations] = useState<Record<string, EvaluationState>>(() => {
    if (savedEvaluations) return savedEvaluations;
    const seeded: Record<string, EvaluationState> = {};
    for (const input of inputs ?? []) {
      if (input.type !== ChecklistItemInputType.evaluation || !input.evaluation) continue;
      seeded[input.key] = {
        key: input.evaluation.key,
        rows: input.evaluation.rows.map((row) => ({ entered: row.entered, verdict: row.verdict })),
        verdict: input.evaluation.verdict,
      };
    }
    return seeded;
  });
  const isPending = status === ChecklistItemStatus.pending && !isOptimisticInProgress;
  const isItemInProgress =
    status !== ChecklistItemStatus.completed &&
    (status === ChecklistItemStatus.inProgress || isOptimisticInProgress);
  const hasInputs = inputs && inputs.length > 0;

  const allRequiredFilled = useMemo<boolean>(() => {
    if (!inputs || inputs.length === 0) return true;
    return inputs.every((input) => {
      if (input.type === ChecklistItemInputType.evaluation) {
        const evaluationFilled =
          evaluations[input.key]?.verdict !== undefined && evaluations[input.key]?.verdict !== 'incomplete';
        if (!evaluationFilled) return false;
        if (input.options && input.options.length > 0) {
          return (
            !!inputData[input.key] ||
            !!pendingFlagSelections[input.key]?.length ||
            !!activeFlagsByInputKey?.[input.key]?.length
          );
        }
        return true;
      }
      if (input.required || input.type === ChecklistItemInputType.requirement) {
        return (
          !!inputData[input.key] ||
          !!pendingFlagSelections[input.key]?.length ||
          !!activeFlagsByInputKey?.[input.key]?.length
        );
      }
      return true;
    });
  }, [inputs, inputData, evaluations, pendingFlagSelections, activeFlagsByInputKey]);

  // ChecklistItemInputs renders evaluation-type inputs using `input.evaluation` as the
  // `view` prop — the persisted, server-loaded view. That never changes after a Check
  // click, so without this merge the displayed verdict/tag/icon would stay stale even
  // though `evaluations` (above) is already updated correctly. Overlay the live
  // entered/verdict values onto a copy of the persisted view, keeping `derived`/`result`
  // (not present on `EvaluationState`) untouched.
  const mergedInputs = useMemo<ChecklistItemInputDefinitionView[] | undefined>(() => {
    if (!inputs) return inputs;
    return inputs.map((input) => {
      if (input.type !== ChecklistItemInputType.evaluation || !input.evaluation) return input;
      const liveState = evaluations[input.key];
      if (!liveState) return input;
      return {
        ...input,
        evaluation: {
          ...input.evaluation,
          verdict: liveState.verdict,
          rows: input.evaluation.rows.map((row, index) => {
            const liveRow = liveState.rows[index];
            if (!liveRow) return row;
            return { ...row, entered: liveRow.entered, verdict: liveRow.verdict };
          }),
        },
      };
    });
  }, [inputs, evaluations]);

  // ChecklistItemInputs needs to see both the in-progress terminal edits (`inputData`) AND
  // any persisted-but-still-pending flag decision draft (only present in `savedData`, since
  // drafts never enter `inputData`) — so it can seed its own chip-rendering state
  // (`selectedFlagValuesByInput`) from the array entries. Terminal edits always win for a
  // given key; a key never holds both shapes at once (mutual exclusivity is enforced above).
  const itemInputsData = useMemo(() => ({ ...savedData, ...inputData }), [savedData, inputData]);

  const flagDecisions = useMemo<FlagDecision[]>(() => {
    if (!inputs) return [];
    const flagTypes = new Set<string>(Object.values(FlagPurposeType));
    return inputs.flatMap<FlagDecision>((input) => {
      if (input.type === ChecklistItemInputType.evaluation) {
        // Evaluations with options use manual decision selection — treat like a requirement input.
        if (input.options && input.options.length > 0) {
          const activeValues = new Set((activeFlagsByInputKey?.[input.key] ?? []).map((flag) => flag.value));
          const pendingValues = (pendingFlagSelections[input.key] ?? []).filter((v) => !activeValues.has(v));
          return pendingValues.flatMap<FlagDecision>((value) => {
            const option = input.options?.find((opt) => opt.value === value);
            if (!option?.decisionType || !flagTypes.has(option.decisionType)) return [];
            return [
              {
                purposeType: option.decisionType as FlagPurposeType,
                label: input.label,
                requirementFlagSettingId: option.requirementFlagSettingId,
                inputKey: input.key,
                optionValue: value,
              },
            ];
          });
        }
        // Evaluations without options produce auto-decisions when the verdict is `fail`.
        const view = input.evaluation;
        const state = evaluations[input.key];
        if (!view || state?.verdict !== 'fail') return [];
        const core = getEvaluationCore(view.key);
        if (!core) return [];
        const rows = view.rows.map((row, index) => {
          const entered = state.rows[index]?.entered ?? row.entered;
          const result = core.computeRow(row.derived, entered, view.params);
          // Recompute the verdict — the view's verdict reflects the persisted (pre-edit) entered values.
          return { ...row, entered, result, verdict: result.verdict };
        });
        return [
          {
            purposeType: core.flagPurposeType,
            label: input.label,
            requirementFlagSettingId: input.requirementFlagSettingId,
            checklistInputKey: input.key,
            note: core.buildFlagReason(rows, view.params),
          },
        ];
      }

      if (!input.options) return [];
      // Only newly-toggled, not-yet-persisted selections count — an option already backed
      // by an active flag chip must never be re-emitted as a new decision (would re-create
      // a Flag for something that already exists).
      const activeValues = new Set((activeFlagsByInputKey?.[input.key] ?? []).map((flag) => flag.value));
      const pendingValues = (pendingFlagSelections[input.key] ?? []).filter((value) => !activeValues.has(value));
      return pendingValues.flatMap<FlagDecision>((value) => {
        const option = input.options?.find((opt) => opt.value === value);
        if (!option?.decisionType || !flagTypes.has(option.decisionType)) return [];
        return [
          {
            purposeType: option.decisionType as FlagPurposeType,
            label: input.label,
            requirementFlagSettingId: input.requirementFlagSettingId,
            inputKey: input.key,
            optionValue: value,
          },
        ];
      });
    });
  }, [inputs, pendingFlagSelections, activeFlagsByInputKey, evaluations]);

  const handleToggle = useCallback(() => onToggleExpand(itemKey), [itemKey, onToggleExpand]);

  const shouldFocusOnExpand = useRef(false);
  const cardContentRef = useRef<HTMLDivElement>(null);

  const handleToggleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !isExpanded) {
        shouldFocusOnExpand.current = true;
      }
    },
    [isExpanded],
  );

  useEffect(() => {
    if (!isExpanded || !shouldFocusOnExpand.current) return;

    shouldFocusOnExpand.current = false;
    cardContentRef.current?.parentElement?.scrollIntoView({
      block: 'start',
    });

    cardContentRef.current?.querySelector<HTMLInputElement>('input:not([disabled])')?.focus({ preventScroll: true });
  }, [isExpanded]);

  // Accumulates every not-yet-persisted change to this item — terminal decisions, flag-
  // producing selections, and evaluation state — between flushes, so a burst of edits across
  // multiple inputs all reach the server as one PATCH rather than the trailing edit silently
  // winning over an update to a different input/evaluation key.
  const pendingDraftDataRef = useRef<Record<string, string | boolean | ChecklistPendingFlagDecision[]>>({});
  const pendingDraftEvaluationsRef = useRef<Record<string, EvaluationState>>({});

  // Reads whatever is currently accumulated in the refs (not captured args) so it produces
  // the same result whether invoked by the safety-net timer, the unmount flush, or an
  // explicit blur/close flush.
  const persistPendingDraft = useCallback(() => {
    const dataUpdates = pendingDraftDataRef.current;
    const evaluationUpdates = pendingDraftEvaluationsRef.current;
    const hasDataUpdates = Object.keys(dataUpdates).length > 0;
    const hasEvaluationUpdates = Object.keys(evaluationUpdates).length > 0;
    if (!onPersistDraft || (!hasDataUpdates && !hasEvaluationUpdates)) return;
    pendingDraftDataRef.current = {};
    pendingDraftEvaluationsRef.current = {};
    // Same field/PATCH the approved path already uses — the item's `status` is left
    // untouched by the caller (ChecklistPanel), a draft never completes the item.
    const mergedData = { ...savedData, ...dataUpdates };
    if (hasEvaluationUpdates) {
      onPersistDraft(itemKey, mergedData, evaluationUpdates);
    } else {
      onPersistDraft(itemKey, mergedData);
    }
  }, [onPersistDraft, savedData, itemKey]);

  // Safety net only — see DRAFT_SAFETY_NET_DEBOUNCE_MS. Scheduling this on every change keeps
  // a pending call registered so the hook's built-in unmount-flush still catches an edit that
  // was never explicitly blurred/closed before the item card unmounted.
  const scheduleDraftSafetyNet = useDebouncedCallback(persistPendingDraft, DRAFT_SAFETY_NET_DEBOUNCE_MS);

  // Flushes immediately, bypassing the safety-net delay — the primary persistence path,
  // triggered by an explicit blur/close signal from ChecklistItemInputs.
  const flushDraftNow = useCallback(() => {
    scheduleDraftSafetyNet.cancel();
    persistPendingDraft();
  }, [scheduleDraftSafetyNet, persistPendingDraft]);

  const handleApprove = useCallback(() => {
    // An authoritative save supersedes any queued draft flush — cancel it so a stale draft
    // PATCH can never race the completion PATCH and revert the item out of `completed`.
    scheduleDraftSafetyNet.cancel();
    pendingDraftDataRef.current = {};
    pendingDraftEvaluationsRef.current = {};
    onApprove(itemKey, inputData, evaluations);
  }, [itemKey, inputData, evaluations, onApprove, scheduleDraftSafetyNet]);

  const handleStartFlagFlow = useCallback(() => {
    scheduleDraftSafetyNet.cancel();
    pendingDraftDataRef.current = {};
    pendingDraftEvaluationsRef.current = {};
    onStartFlagFlow(itemKey, inputData, flagDecisions, evaluations);
  }, [itemKey, inputData, flagDecisions, evaluations, onStartFlagFlow, scheduleDraftSafetyNet]);

  const handleUnreview = useCallback(() => onUnreview(itemKey), [itemKey, onUnreview]);

  const handleInputChange = useCallback(
    (newData: Record<string, string | boolean>) => {
      setInputData(newData);
      // A terminal option is exclusive with flag-producing options on the same input — the
      // moment a terminal value is set for a key, clear any pending flag toggles on it.
      setPendingFlagSelections((prev) => {
        const changedKeys = Object.keys(newData).filter(
          (key) => newData[key] && newData[key] !== inputData[key] && (prev[key]?.length ?? 0) > 0,
        );
        if (changedKeys.length === 0) return prev;
        const next = { ...prev };
        for (const key of changedKeys) next[key] = [];
        return next;
      });
      // `newData` is already the full current terminal-only bag (see ChecklistItemInputs'
      // `handleChange`), so merging it in overwrites exactly the keys that changed, including
      // clearing out any stale ChecklistPendingFlagDecision[] entry at the same key.
      pendingDraftDataRef.current = { ...pendingDraftDataRef.current, ...newData };
      scheduleDraftSafetyNet();
    },
    [inputData, scheduleDraftSafetyNet],
  );

  const handleEvaluationChange = useCallback(
    (inputKey: string, state: EvaluationState) => {
      setEvaluations((prev) => ({ ...prev, [inputKey]: state }));
      pendingDraftEvaluationsRef.current = { ...pendingDraftEvaluationsRef.current, [inputKey]: state };
      scheduleDraftSafetyNet();
    },
    [scheduleDraftSafetyNet],
  );

  const handleFlagSelectionsChange = useCallback((inputKey: string, values: string[]) => {
    setPendingFlagSelections((prev) => ({ ...prev, [inputKey]: values }));
    // A flag-producing selection is exclusive with a terminal decision on the same input —
    // clear any terminal value that's currently set for it.
    setInputData((prev) => {
      if (!prev[inputKey]) return prev;
      const { [inputKey]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const handlePendingFlagSelectionsChange = useCallback(
    (inputKey: string, decisions: ChecklistPendingFlagDecision[]) => {
      pendingDraftDataRef.current = { ...pendingDraftDataRef.current, [inputKey]: decisions };
      scheduleDraftSafetyNet();
    },
    [scheduleDraftSafetyNet],
  );

  const handleRemoveActiveFlag = useCallback(
    (_inputKey: string, flagId: string) => {
      onRemoveFlag?.(flagId);
    },
    [onRemoveFlag],
  );

  const { activeId: activeTooltipId, openTooltip, closeTooltip } = useChecklistTooltip();
  const isTooltipOpen = activeTooltipId === itemKey;

  const handleTooltipIconClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isTooltipOpen) {
        closeTooltip();
      } else if (tooltipText) {
        openTooltip(itemKey, tooltipText);
      }
    },
    [isTooltipOpen, closeTooltip, openTooltip, itemKey, tooltipText],
  );

  const containerClass = isExpanded
    ? 'bg-canvas-bold border-2 border-[var(--chip-canvas-selected-pressed)] rounded-[8px] px-[18px] py-[14px] flex flex-col gap-[10px]'
    : 'border-b-2 border-[var(--togglecard-neutral-border-idle)] pt-[12px] pb-[14px] px-[16px] flex flex-col gap-[10px] bg-canvas-subtle';

  return (
    <div className={containerClass}>
      {aiCompleted && aiSummary && (
        <div className="flex items-center gap-6 self-start rounded-20 bg-chip-canvas-default-pressed px-10 py-4">
          <PalmettoIcon name="sparkle" size="sm" />
          <span className="text-sm font-bold text-text-tertiary">Chiro: {aiSummary}</span>
        </div>
      )}

      <div className="flex items-start gap-6">
        <button
          type="button"
          className="flex flex-1 flex-col gap-4 text-left"
          onClick={handleToggle}
          onKeyDown={handleToggleKeyDown}
        >
          <div className="flex w-full items-center gap-6">
            <span className={`text-base font-bold ${isExpanded ? 'text-text-primary' : 'text-text-secondary'}`}>
              {name}
            </span>
            <PalmettoIcon name="chevronRight" size="sm" className="shrink-0 text-text-secondary" />
            {status === ChecklistItemStatus.completed && (flagCount ?? 0) > 0 && (
              <div className="ml-auto">
                <PalmettoTag
                  color="error"
                  label={`${flagCount} flag${(flagCount ?? 0) > 1 ? 's' : ''}`}
                  size="sm"
                  icon="flagFilled"
                />
              </div>
            )}
            {/* Only show "No issues" when the flag count is explicitly known to be 0 —
              an undefined count means "not computed yet", which must not read as "no issues". */}
            {status === ChecklistItemStatus.completed && flagCount === 0 && (
              <div className="ml-auto">
                <PalmettoTag color="success" label="No issues" size="sm" icon="check" />
              </div>
            )}
          </div>
          <div className="text-xs text-text-primary">{description}</div>
        </button>

        {isExpanded && tooltipText && (
          <div className="shrink-0">
            <button
              type="button"
              aria-label="Open guidance"
              onClick={handleTooltipIconClick}
              className="mt-4 text-text-tertiary hover:text-text-primary"
            >
              <PalmettoIcon name="infoSquare" size="sm" />
            </button>
          </div>
        )}
      </div>

      {isExpanded && (
        <div ref={cardContentRef} className="flex flex-col gap-10">
          {hasInputs && (
            <ChecklistItemInputs
              inputs={mergedInputs}
              data={isPending ? itemInputsData : (savedData ?? EMPTY_ITEM_DATA)}
              onChange={handleInputChange}
              onEvaluationChange={handleEvaluationChange}
              onFlagSelectionsChange={handleFlagSelectionsChange}
              onPendingFlagSelectionsChange={handlePendingFlagSelectionsChange}
              onDraftFlush={flushDraftNow}
              activeFlagsByInput={activeFlagsByInputKey}
              onRemoveActiveFlag={handleRemoveActiveFlag}
              readOnly={!isPending}
              itemKey={itemKey}
            />
          )}

          {isPending && allRequiredFilled && flagDecisions.length > 0 && (flagCount ?? 0) === 0 && (
            <button
              type="button"
              aria-label={flagDecisions.length > 1 ? `Create ${flagDecisions.length} flags` : 'Create flag'}
              disabled={isUpdating}
              onClick={handleStartFlagFlow}
              className={PRIMARY_ACTION_BUTTON}
            >
              <span className={COMPLETE_LABEL_CLASS}>
                {flagDecisions.length > 1 ? `Create ${flagDecisions.length} flags` : 'Create flag'}
              </span>
            </button>
          )}

          {isPending && allRequiredFilled && (flagDecisions.length === 0 || (flagCount ?? 0) > 0) && (
            <button
              type="button"
              aria-label="Complete"
              disabled={isUpdating}
              onClick={handleApprove}
              className={SUCCESS_ACTION_BUTTON}
            >
              <span className={COMPLETE_LABEL_CLASS}>Complete</span>
            </button>
          )}

          {isItemInProgress && (
            <button
              type="button"
              aria-label="Complete"
              disabled={isUpdating}
              onClick={handleApprove}
              className={SUCCESS_ACTION_BUTTON}
            >
              <span className={COMPLETE_LABEL_CLASS}>Complete</span>
            </button>
          )}

          {status === ChecklistItemStatus.completed && (
            <button
              type="button"
              aria-label="Unreview"
              disabled={isUpdating}
              onClick={handleUnreview}
              className="flex items-center justify-center rounded-16 border-2 border-border-default px-20 py-[14px] font-bold text-text-secondary disabled:opacity-50"
            >
              Unreview
            </button>
          )}

          {status === ChecklistItemStatus.skipped && <PalmettoTag label="Flagged" color="error" size="sm" />}
        </div>
      )}
    </div>
  );
};
